import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, PhoneCall, MessageCircle } from "lucide-react";
import { get, ref } from "firebase/database";

import { database } from "../../firebase/firebase";
import LoadingScreen from "../../utils/LoadingScreen";
import BookingCard from "../components/booking/BookingCard";
import { Title } from "../components/settings/Title";
import { Item } from "../components/settings/Item";
import { Button } from "../../components/ui/button";
import { useCatalog } from "../../contexts/CatalogContext";

const exchangeRates: Record<string, number> = {
  dollar: 0.16,
  euro: 0.16,
  pesos: 166.21,
  real: 1,
};

export default function ClientBookingsPage() {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<any[]>([]);

  const { phone } = useParams<{ phone: string }>();
  const navigate = useNavigate();
  const { driversMap, vehiclesMap } = useCatalog();

  useEffect(() => {
    const fetchBookings = async () => {
      if (!phone) return;
      try {
        // Normalizar o telefone para comparação (remover caracteres não numéricos)
        const normalizedPhone = phone.replace(/[^\d]/g, "");

        // Buscar todos os bookings e filtrar no cliente para evitar necessidade de índice
        const bookingsRef = ref(database, "/booking");
        const snapshot = await get(bookingsRef);
        if (snapshot.exists()) {
          const allBookings = snapshot.val();
          const clientBookings = Object.entries(allBookings)
            .map(([id, booking]: [string, any]) => ({
              ...booking,
              id,
            }))
            .filter((booking: any) => {
              // Normalizar o telefone do booking para comparação
              const bookingPhone =
                booking.phone?.toString().replace(/[^\d]/g, "") || "";
              return bookingPhone === normalizedPhone;
            })
            .sort((a, b) => a.date - b.date);
          setBookings(clientBookings);
        }
      } catch (error) {
        console.error("Error fetching client bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [phone]);

  const totalValue = useMemo(() => {
    return bookings.reduce((sum, booking) => {
      const price = parseFloat(booking.value || "0");
      const currency = booking.currency
        ? booking.currency.toLowerCase()
        : "real";
      const rate = exchangeRates[currency] || 1;
      return sum + price / rate;
    }, 0);
  }, [bookings]);

  if (loading || !bookings.length) {
    return <LoadingScreen />;
  }

  const firstBooking = bookings[0];
  const goBack = () => navigate("/admin-management/others/clients");

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-white">
      <header className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={goBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">
            Cliente
          </p>
          <h1 className="text-2xl font-semibold">{firstBooking.name}</h1>
        </div>
      </header>

      <div className="space-y-4">
        <Title>Informações</Title>
        <Item className="space-y-2 bg-white/5 text-white">
          <p className="text-sm text-white/60">Nome</p>
          <p className="text-lg font-semibold">{firstBooking.name}</p>
          <p className="text-sm text-white/60">Telefone</p>
          <p className="text-lg font-semibold">{firstBooking.phone}</p>
        </Item>
        <div className="grid gap-4 md:grid-cols-2">
          <Item className="text-center text-white">
            <p className="text-3xl font-semibold">{bookings.length}</p>
            <p className="text-sm text-white/60">Viagens</p>
          </Item>
          <Item className="text-center text-white">
            <p className="text-3xl font-semibold">R$ {totalValue.toFixed(0)}</p>
            <p className="text-sm text-white/60">Total gasto</p>
          </Item>
        </div>
        {bookings.length > 1 && (
          <Item className="text-center text-white">
            <p className="text-3xl font-semibold">
              ~ R$ {(totalValue / bookings.length).toFixed(0)}
            </p>
            <p className="text-sm text-white/60">Média por viagem</p>
          </Item>
        )}

        <div className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">
            Contato
          </p>
          <div className="flex flex-col gap-3 md:flex-row">
            <Button
              variant="outline"
              className="w-full border-white/20 bg-white/10 text-white hover:bg-white/20"
              asChild
            >
              <a href={`tel://${firstBooking.phone}`}>
                <PhoneCall className="mr-2 h-4 w-4" /> Telefone
              </a>
            </Button>
            <Button
              className="w-full bg-emerald-500 text-white hover:bg-emerald-500/90"
              onClick={() =>
                window.open(
                  `https://wa.me/${firstBooking.phone
                    .toString()
                    .replace(/[^\d]/g, "")}`,
                )
              }
            >
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
          </div>
        </div>

        <Title>Viagens</Title>
        <div className="grid gap-4 md:grid-cols-2">
          {bookings.map((booking: any) => (
            <BookingCard
              booking={booking}
              key={booking.id}
              done={false}
              driver={driversMap[booking.driver]}
              vehicle={vehiclesMap[booking.vehicle]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
